import React from 'react';
import {
  DIVIDER_ORIENTATION_HORIZONTAL,
  type DividerOrientation,
} from './Divider.constants';
import { Root } from './Divider.styled';

export interface DividerProps {
  /** `'horizontal'` (default) draws a row separator; `'vertical'` a hairline column splitter for inline toolbars. */
  orientation?: DividerOrientation;
  /** Hairline width in px. Defaults to 1. */
  thickness?: number;
  /**
   * Cross-axis size. Vertical default is 18px (matches the toolbar prototype);
   * horizontal default is 100% of its container. Accepts numbers (px) or any
   * CSS length string.
   */
  length?: number | string;
  /** Margin on the divider's main axis (px or any CSS length). Defaults to 4. */
  inset?: number | string;
  className?: string;
  dataTestId?: string;
}

/**
 * Faint hairline divider — used in toolbars (vertical) to fence off groups
 * of controls, and inline (horizontal) anywhere a section break is needed.
 *
 * Renders as `role="separator"` so screen readers announce it correctly.
 */
const Divider: React.FC<DividerProps> = ({
  orientation = DIVIDER_ORIENTATION_HORIZONTAL,
  thickness = 1,
  length,
  inset = 4,
  className,
  dataTestId = 'divider',
}) => (
  <Root
    role="separator"
    aria-orientation={orientation}
    $orientation={orientation}
    $thickness={thickness}
    $length={length}
    $inset={inset}
    className={className}
    data-testid={dataTestId}
  />
);

export default Divider;
